import { ChordLib } from '@tunasong/music-lib'
import type { Chord, CadenceOption } from '@tunasong/music-lib'
import type { FC } from 'react'
import { useState } from 'react'
import { TheoryCard } from '../window-tag/theory-card.js'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import { useCadenceLabPlayback } from './cadence-lab-playback.js'
import styles from './cadence-option-card.module.css'

export type CadenceCardKey = 'major' | 'minor'

/**
 * Bowie's §8 Q5 suppression captions, level-resolved. `{Mode}` is substituted
 * with the capitalized mode name (Mixolydian / Dorian / Phrygian).
 *
 * Lydian is handled by its own copy block — IV is diminished there, the
 * mechanism is different from the modal-no-leading-tone case.
 */
const SUPPRESSION_COPY = {
  'modal-no-leading-tone': {
    beginner: () => `Not available here — this mode doesn't have a strong pull home.`,
    intermediate: (mode: string) =>
      `Not available in ${mode} — the dominant chord is minor, so this cadence doesn't apply.`,
    advanced: (mode: string) =>
      `Suppressed in ${mode} — the diatonic v lacks the leading tone needed for authentic / deceptive resolution.`,
  },
  'lydian-diminished-iv': {
    beginner: () => `Not available here — Lydian's IV is unusual.`,
    intermediate: () => `Not available in Lydian — the IV chord is diminished (♯IV°).`,
    advanced: () => `Suppressed in Lydian — ♯IV° doesn't function as a stable plagal predominant.`,
  },
} as const

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

export interface CadenceOptionCardProps {
  option: CadenceOption
  prefix: Chord[]
  /** Mode display name used to fill `{Mode}` in suppression captions. */
  modeName?: string
  /**
   * True when the user's scale is a minor KEY (`scale.type === 'minor'`).
   * Drives the Q3 deceptive-tagLabel override (V → vi → V → ♭VI) at advanced.
   */
  isMinorKey?: boolean
}

export const CadenceOptionCard: FC<CadenceOptionCardProps> = props => {
  const { option, prefix, modeName, isMinorKey } = props
  const copy = useTheoryCopy(option.lexiconId)
  const [whyOpen, setWhyOpen] = useState(false)
  const { play } = useCadenceLabPlayback({
    prefix,
    endingChord: option.endingChord,
    penultSubstitution: option.penultSubstitution,
  })

  const heading = copy?.displayNameShort ?? capitalize(option.kind)

  if (!option.available) {
    const suppressionReason = option.suppressionReason ?? 'modal-no-leading-tone'
    const level = copy?.level ?? 'intermediate'
    const captionFn = SUPPRESSION_COPY[suppressionReason][level]
    const caption = captionFn(modeName ?? '')
    return (
      <section
        className={styles.cardDisabled}
        aria-label={`${heading} (not available)`}
        data-testid={`cadence-card-${option.kind}-disabled`}
      >
        <h3 className={styles.heading}>{heading}</h3>
        <p className={styles.suppression}>{caption}</p>
      </section>
    )
  }

  // Q3 deceptive override: in minor KEYS, advanced tagLabel becomes "V → ♭VI".
  const baseTag = copy?.tag ?? ''
  const tagLabel =
    option.kind === 'deceptive' && isMinorKey && copy?.level === 'advanced' ? 'V → ♭VI' : baseTag
  const endingName = option.endingChord ? ChordLib.getName(option.endingChord) : ''

  return (
    <section className={styles.card} aria-label={heading} data-testid={`cadence-card-${option.kind}`}>
      <h3 className={styles.heading}>{heading}</h3>
      <p className={styles.endingChord}>{endingName}</p>
      {tagLabel ? <p className={styles.tagLabel}>{tagLabel}</p> : null}
      <div className={styles.actions}>
        <button type="button" aria-label="Play" onClick={play}>
          Play
        </button>
        <button
          type="button"
          aria-label="Why?"
          aria-expanded={whyOpen}
          onClick={() => setWhyOpen(o => !o)}
        >
          Why?
        </button>
      </div>
      {whyOpen ? <TheoryCard lexiconId={option.lexiconId} /> : null}
    </section>
  )
}
